/**
 * Pure mapping from the wire `RoundMsg` to the client's round state.
 *
 * Split out of Connection so the message-to-state mapping is testable without
 * a socket: feed it whatever `decodeServerMessage` produces and check what
 * comes out, the same way the shared package tests its own pure functions.
 */

import {
  PlayerFlag,
  type RoundMsg,
  type RoundPhaseId,
  type RoundState,
  type TeamId,
} from "@prop-hunt/shared";

export function roundStateFromMsg(msg: RoundMsg): RoundState {
  return {
    // Wire value: a u8 the server picked from RoundPhase, decoded here as a
    // plain number the same way decodeServerMessage leaves it.
    phase: msg.phase as RoundPhaseId,
    phaseStartedTick: msg.phaseStartedTick,
    roundNumber: msg.roundNumber,
    huntersWon: msg.huntersWon,
    hidersWon: msg.hidersWon,
  };
}

/** Every connected player's team, including the recipient's own. */
export function teamsFromMsg(msg: RoundMsg): Map<number, TeamId> {
  const teams = new Map<number, TeamId>();
  for (const t of msg.teams) teams.set(t.id, t.team as TeamId);
  return teams;
}

/**
 * Who has been found, as of this message.
 *
 * Read from the roster rather than accumulated from `Msg.Tag` events, which is
 * the whole reason the flag is in the roster: a client that missed a Tag — or
 * joined after one — is correct from its very first Round message rather than
 * carrying a permanent hole. Returned as a set of ids so a caller replaces its
 * previous answer wholesale instead of merging into it.
 */
export function foundFromMsg(msg: RoundMsg): Set<number> {
  const found = new Set<number>();
  for (const t of msg.teams) {
    if ((t.flags & PlayerFlag.Found) !== 0) found.add(t.id);
  }
  return found;
}
